import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Block as BlockIcon,
  Warning as WarningIcon,
  FindReplace as ProbeIcon
} from '@mui/icons-material';

const ApprovedModelsTable = ({ models, onEdit, onDeactivate, onDeprecate, onDelete, onViewUsage, onDetectCapabilities }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const getModelStatus = (model) => {
    if (model.deprecatedAt) return 'deprecated';
    if (!model.isActive) return 'inactive';
    return 'active';
  };

  const filteredModels = useMemo(() => {
    let filtered = models || []; // Handle undefined models
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.name?.toLowerCase().includes(search) ||
          m.provider?.toLowerCase().includes(search) ||
          m.modelId?.toLowerCase().includes(search)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter((m) => getModelStatus(m) === statusFilter);
    }
    
    // Ensure filtered is always an array before sorting
    return Array.isArray(filtered) ? filtered.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)) : [];
  }, [models, searchTerm, statusFilter]);

  const renderStatusBadge = (model) => {
    const status = getModelStatus(model);
    if (status === 'active') return <Chip label="Active" color="success" size="small" />;
    if (status === 'deprecated') return <Chip label="Deprecated" color="warning" size="small" />;
    return <Chip label="Inactive" color="default" size="small" />;
  };

  const renderFilters = () => (
    <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
      <TextField
        label="Search"
        variant="outlined"
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search by name or provider..."
        sx={{ flexGrow: 1 }}
      />
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Status</InputLabel>
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="deprecated">Deprecated</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );

  if (filteredModels.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        {renderFilters()}
        <Typography variant="body1" color="text.secondary" align="center">
          No approved models found
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {renderFilters()}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Model ID</TableCell>
              <TableCell align="right">Max Tokens</TableCell>
              <TableCell align="right">Temperature</TableCell>
              <TableCell align="center">Docs</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredModels.map((model) => (
              <TableRow key={model.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {model.name}
                  </Typography>
                </TableCell>
                <TableCell>{model.provider}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {model.modelId}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {model.defaultMaxTokens || model.maxTokensLimit}
                </TableCell>
                <TableCell align="right">{model.recommendedTemperature}</TableCell>
                <TableCell align="center">
                  <Tooltip title={model.supportsDocuments ? 'Supports PDF documents' : 'No document support — PDFs converted to text via OCR'}>
                    <Chip
                      label={model.supportsDocuments ? 'Yes' : 'No'}
                      color={model.supportsDocuments ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>{renderStatusBadge(model)}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={() => onEdit(model)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Deactivate">
                    <IconButton size="small" color="warning" onClick={() => onDeactivate(model)}>
                      <BlockIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Deprecate">
                    <IconButton size="small" color="warning" onClick={() => onDeprecate(model)}>
                      <WarningIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => onDelete(model)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="View Usage">
                    <IconButton size="small" color="info" onClick={() => onViewUsage(model)}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Detect Document Support">
                    <IconButton size="small" color="secondary" onClick={() => onDetectCapabilities(model)}>
                      <ProbeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

ApprovedModelsTable.propTypes = {
  models: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      modelId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      provider: PropTypes.string.isRequired,
      defaultMaxTokens: PropTypes.number,
      maxTokensLimit: PropTypes.number.isRequired,
      recommendedTemperature: PropTypes.number.isRequired,
      isActive: PropTypes.bool.isRequired,
      deprecatedAt: PropTypes.string,
      displayOrder: PropTypes.number.isRequired
    })
  ).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDeactivate: PropTypes.func.isRequired,
  onDeprecate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onViewUsage: PropTypes.func.isRequired,
  onDetectCapabilities: PropTypes.func.isRequired
};

export default ApprovedModelsTable;
